import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthPaidMediaTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { GrowthCampaign, GrowthMarketingService } from '../growth-marketing.service';
import {
  GROWTH_PAID_MEDIA_OBJECTIVES,
  GrowthPaidMediaAccountDto,
  GrowthPaidMediaCampaignLinkDto,
  accountDisplayLabel
} from './growth-paid-media.models';
import { GrowthPaidMediaService } from './growth-paid-media.service';

@Component({
  standalone: true,
  selector: 'ain-growth-paid-media-campaign-links',
  imports: [
    AinPageComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    TableModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    DialogModule,
    RouterLink
  ],
  template: `
    <ain-page [title]="t('growth.paidMedia.campaignLinks.title')" [description]="t('growth.paidMedia.campaignLinks.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.paidMedia.campaignLinks.help.title'"
        [descriptionKey]="'growth.paidMedia.campaignLinks.help.description'"
        [bulletKeys]="['growth.paidMedia.campaignLinks.help.bullets.budget', 'growth.paidMedia.campaignLinks.help.bullets.live']" />

      <section class="card list-card">
        <div class="toolbar">
          <p-select
            [options]="accountOptions()"
            [(ngModel)]="accountFilter"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            [filter]="true"
            appendTo="body"
            [placeholder]="t('growth.paidMedia.campaignLinks.filterAccount')" />
          <button pButton type="button" [label]="t('growth.paidMedia.campaignLinks.search')" (click)="load()"></button>
          <button pButton type="button" [label]="t('growth.paidMedia.campaignLinks.refresh')" severity="secondary" (click)="load()"></button>
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
        </div>
        <div class="toolbar">
          <button
            pButton
            type="button"
            [label]="t('growth.paidMedia.campaignLinks.actions.openSelected')"
            severity="secondary"
            [disabled]="!selected()"
            (click)="openSelectedDetail()"></button>
          <button pButton type="button" [label]="t('growth.paidMedia.campaignLinks.actions.new')" (click)="openCreateDialog()"></button>
          <a class="link-btn" routerLink="/growth/paid-media/accounts">{{ t('growth.paidMedia.campaignLinks.openAccounts') }}</a>
          <a class="link-btn" routerLink="/growth/paid-media/roas">{{ t('growth.paidMedia.campaignLinks.openRoas') }}</a>
        </div>
        <p-table
          [value]="rows()"
          selectionMode="single"
          [selection]="selected()"
          (selectionChange)="onSelect($event)"
          dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.paidMedia.campaignLinks.columns.campaign') }}</th>
              <th>{{ t('growth.paidMedia.campaignLinks.columns.account') }}</th>
              <th>{{ t('growth.paidMedia.campaignLinks.columns.objective') }}</th>
              <th>{{ t('growth.paidMedia.campaignLinks.columns.budgetCap') }}</th>
              <th>{{ t('growth.paidMedia.campaignLinks.columns.requestedSpend') }}</th>
              <th>{{ t('growth.paidMedia.campaignLinks.columns.approvalState') }}</th>
              <th>{{ t('growth.paidMedia.campaignLinks.columns.status') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [pSelectableRow]="row">
              <td>
                <button type="button" class="link-btn" (click)="openDetail(row.id)">{{ campaignLabel(row.growthCampaignId) }}</button>
              </td>
              <td>{{ accountLabel(row.accountId) }}</td>
              <td>{{ objectiveLabel(row.objective) }}</td>
              <td>{{ row.budgetCap }}</td>
              <td>{{ row.requestedSpend ?? '-' }}</td>
              <td>{{ approvalLabel(row.approvalState) }}</td>
              <td>{{ row.status }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      </section>
    </ain-page>

    <p-dialog
      [header]="t('growth.paidMedia.campaignLinks.createDialog.title')"
      [visible]="createDialogVisible()"
      (visibleChange)="createDialogVisible.set($event)"
      [modal]="true">
      <ain-info-hint
        [titleKey]="'growth.paidMedia.campaignLinks.createDialog.help.title'"
        [descriptionKey]="'growth.paidMedia.campaignLinks.createDialog.help.description'" />
      <div class="dialog-grid">
        <ain-form-field [label]="t('growth.paidMedia.campaignLinks.fields.account')">
          <p-select
            [options]="accountOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="createModel.accountId"
            [filter]="true"
            appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.paidMedia.campaignLinks.fields.campaign')">
          <p-select
            [options]="campaignOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="createModel.growthCampaignId"
            [filter]="true"
            appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.paidMedia.campaignLinks.fields.objective')">
          <p-select
            [options]="objectiveOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="createModel.objective"
            appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.paidMedia.campaignLinks.fields.budgetCap')">
          <p-inputNumber [(ngModel)]="createModel.budgetCap" [min]="0" mode="decimal" [minFractionDigits]="0" [maxFractionDigits]="2" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.paidMedia.campaignLinks.fields.externalCampaignId')" class="full">
          <input pInputText type="text" [(ngModel)]="createModel.externalCampaignId" [placeholder]="t('growth.paidMedia.campaignLinks.fields.externalCampaignIdPlaceholder')" />
        </ain-form-field>
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="createDialogVisible.set(false)"></button>
        <button pButton type="button" [label]="t('common.save')" [loading]="savingCreate()" (click)="saveCreate()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; text-decoration: none; }
    .link-btn:hover { text-decoration: underline; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 10px; }
    .dialog-grid .full { grid-column: 1 / -1; }
  `]
})
export class GrowthPaidMediaCampaignLinksComponent implements OnInit {
  private readonly api = inject(GrowthPaidMediaService);
  private readonly marketing = inject(GrowthMarketingService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly rows = signal<GrowthPaidMediaCampaignLinkDto[]>([]);
  readonly accounts = signal<GrowthPaidMediaAccountDto[]>([]);
  readonly campaigns = signal<GrowthCampaign[]>([]);
  readonly selected = signal<GrowthPaidMediaCampaignLinkDto | null>(null);
  readonly errorMessage = signal('');
  readonly createDialogVisible = signal(false);
  readonly savingCreate = signal(false);

  accountFilter: string | null = null;
  createModel = {
    accountId: null as string | null,
    growthCampaignId: null as string | null,
    objective: 'awareness',
    budgetCap: 1000,
    externalCampaignId: ''
  };

  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.accountFilter = this.route.snapshot.queryParamMap.get('accountId');

    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthPaidMediaChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());

    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.loadLookups();
    this.load();
  }

  accountOptions(): Array<{ label: string; value: string }> {
    return this.api.accountOptions(this.accounts());
  }

  campaignOptions(): Array<{ label: string; value: string }> {
    return this.campaigns().map(item => ({ value: item.id, label: item.name }));
  }

  objectiveOptions(): Array<{ label: string; value: string }> {
    return GROWTH_PAID_MEDIA_OBJECTIVES.map(value => ({ value, label: this.objectiveLabel(value) }));
  }

  accountLabel(accountId: string): string {
    const account = this.accounts().find(item => item.id === accountId);
    return account ? accountDisplayLabel(account) : this.t('growth.paidMedia.common.unknownAccount');
  }

  campaignLabel(campaignId: string): string {
    return this.campaigns().find(item => item.id === campaignId)?.name
      ?? this.t('growth.paidMedia.common.unknownCampaign');
  }

  objectiveLabel(value: string): string {
    return this.t(`growth.paidMedia.objectives.${value}`);
  }

  approvalLabel(value: string): string {
    return this.t(`growth.paidMedia.approvalState.${value}`);
  }

  loadLookups(): void {
    this.api.listAccounts().subscribe({
      next: rows => this.accounts.set(rows),
      error: () => this.accounts.set([])
    });
    this.marketing.listCampaigns().subscribe({
      next: rows => this.campaigns.set(rows),
      error: () => this.campaigns.set([])
    });
  }

  load(): void {
    this.errorMessage.set('');
    this.api.listCampaignLinks(this.accountFilter).subscribe({
      next: data => {
        this.rows.set(data);
        const currentId = this.selected()?.id;
        this.selected.set(data.find(item => item.id === currentId) ?? data[0] ?? null);
      },
      error: () => {
        this.rows.set([]);
        this.selected.set(null);
        this.errorMessage.set(this.t('growth.paidMedia.campaignLinks.messages.loadFailed'));
      }
    });
  }

  onSelect(row: GrowthPaidMediaCampaignLinkDto | null): void {
    this.selected.set(row);
  }

  openSelectedDetail(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.openDetail(row.id);
  }

  openDetail(linkId: string): void {
    void this.router.navigate(['/growth/paid-media/campaign-links', linkId]);
  }

  openCreateDialog(): void {
    this.createModel = {
      accountId: this.accountFilter ?? this.accounts()[0]?.id ?? null,
      growthCampaignId: this.campaigns()[0]?.id ?? null,
      objective: 'awareness',
      budgetCap: 1000,
      externalCampaignId: ''
    };
    this.createDialogVisible.set(true);
  }

  saveCreate(): void {
    if (!this.createModel.accountId || !this.createModel.growthCampaignId) {
      this.errorMessage.set(this.t('growth.paidMedia.campaignLinks.messages.validationFailed'));
      return;
    }
    this.savingCreate.set(true);
    this.errorMessage.set('');
    this.api.createCampaignLink({
      accountId: this.createModel.accountId,
      growthCampaignId: this.createModel.growthCampaignId,
      objective: this.createModel.objective,
      budgetCap: this.createModel.budgetCap,
      externalCampaignId: this.createModel.externalCampaignId.trim() || null
    }).pipe(finalize(() => this.savingCreate.set(false))).subscribe({
      next: created => {
        this.createDialogVisible.set(false);
        this.load();
        void this.router.navigate(['/growth/paid-media/campaign-links', created.id]);
      },
      error: () => this.errorMessage.set(this.t('growth.paidMedia.campaignLinks.messages.createFailed'))
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthPaidMediaTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
